import { FC, useState } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CollapsibleContainer,
  FormInputField,
  FormSelectField,
  Heading,
  Icon,
  Paragraph,
} from '@centrix/shared-ui';
import { ApiError, useCurrentUser, useEnterpriseUuid, useInviteLearner } from '@centrix/api-client';
import { getMfeAppUrl, isAdminUser } from '@centrix/types';
import { arrowRightIcon, plusIcon, uploadIcon } from '@centrix/shared-ui/src/icons';

import logoImage from '@/assets/logo.png';

import { buildOnboardingPath, resolveOnboardingEmail } from './onboarding';

const ADMIN_REDIRECT = getMfeAppUrl('admin', {
  env: import.meta.env,
  isDev: import.meta.env.DEV,
});
const LMS_BASE_URL = import.meta.env.VITE_LMS_BASE_URL || 'http://localhost:18000';
const LEARNER_REDIRECT = `${LMS_BASE_URL}/learner-dashboard/`;

const FORM_LABEL_CLASS_NAME = 'mb-1 text-xs text-gray-placeholder';
const FORM_INPUT_CLASS_NAME =
  'h-11 rounded px-4 py-2 text-sm text-black placeholder:text-gray-placeholder focus-visible:ring-offset-0 border-gray-placeholder';
const FORM_SELECT_TRIGGER_CLASS_NAME =
  'h-11 rounded px-4 py-2 text-sm text-gray-placeholder data-[placeholder]:text-gray-placeholder border-gray-placeholder';

const MISSING_ENTERPRISE_MESSAGE =
  'Unable to determine your enterprise for learner invitations. Please sign in again.';

// TODO: Replace with backend-driven options once Sector / Role Category /
// Employment Status are part of the learner invitation API. See
// `backend.request.md` ("Learner invite — Complete Profile step 2 fields").
const SECTOR_OPTIONS: { value: string; label: string }[] = [];
const ROLE_CATEGORY_OPTIONS: { value: string; label: string }[] = [];
const EMPLOYMENT_STATUS_OPTIONS: { value: string; label: string }[] = [];

function getPostOnboardingRedirect(currentUser: ReturnType<typeof useCurrentUser>['data']) {
  return currentUser && isAdminUser(currentUser) ? ADMIN_REDIRECT : LEARNER_REDIRECT;
}

let uid = 0;
const nextUid = () => ++uid;

interface LearnerEntry {
  _uid: number;
  name: string;
  username: string;
  email: string;
  contactNumber: string;
  sector: string;
  roleCategory: string;
  employmentStatus: string;
}

const emptyLearner = (): LearnerEntry => ({
  _uid: nextUid(),
  name: '',
  username: '',
  email: '',
  contactNumber: '',
  sector: '',
  roleCategory: '',
  employmentStatus: '',
});

interface LearnerFieldErrors {
  email?: string;
}

const LearnerForm: FC<{
  index: number;
  learner: LearnerEntry;
  onChange: (updated: LearnerEntry) => void;
  disabled: boolean;
  fieldErrors?: LearnerFieldErrors;
  onClearError?: (field: keyof LearnerFieldErrors) => void;
}> = ({ index, learner, onChange, disabled, fieldErrors = {}, onClearError }) => {
  const fieldPrefix = `learner-${learner._uid}`;
  const update = (field: keyof Omit<LearnerEntry, '_uid'>, value: string) =>
    onChange({ ...learner, [field]: value });

  return (
    <div className="space-y-2.5 animate-fade-in-down">
      <Paragraph className="text-lg font-medium text-black leading-6">
        Learner {index + 1}
      </Paragraph>

      <div className="grid grid-cols-2 gap-2.5">
        <FormInputField
          id={`${fieldPrefix}-name`}
          label="Learner Name"
          value={learner.name}
          onChange={(e) => update('name', e.target.value)}
          placeholder="Learner Name"
          disabled={disabled}
          labelClassName={FORM_LABEL_CLASS_NAME}
          inputClassName={FORM_INPUT_CLASS_NAME}
        />
        <FormInputField
          id={`${fieldPrefix}-username`}
          label="Username"
          value={learner.username}
          onChange={(e) => update('username', e.target.value)}
          placeholder="Username"
          disabled={disabled}
          labelClassName={FORM_LABEL_CLASS_NAME}
          inputClassName={FORM_INPUT_CLASS_NAME}
        />
      </div>

      <FormInputField
        id={`${fieldPrefix}-email`}
        type="email"
        label="Learner Email"
        value={learner.email}
        onChange={(e) => {
          update('email', e.target.value);
          onClearError?.('email');
        }}
        placeholder="Learner Email"
        disabled={disabled}
        error={fieldErrors.email}
        labelClassName={FORM_LABEL_CLASS_NAME}
        inputClassName={FORM_INPUT_CLASS_NAME}
      />

      <div className="grid grid-cols-2 gap-2.5">
        <FormInputField
          id={`${fieldPrefix}-contact`}
          type="tel"
          label="Contact Number"
          value={learner.contactNumber}
          onChange={(e) => update('contactNumber', e.target.value)}
          placeholder="Contact Number"
          disabled={disabled}
          labelClassName={FORM_LABEL_CLASS_NAME}
          inputClassName={FORM_INPUT_CLASS_NAME}
        />
        <FormSelectField
          id={`${fieldPrefix}-sector`}
          label="Sector"
          value={learner.sector}
          onValueChange={(v) => update('sector', v)}
          options={SECTOR_OPTIONS}
          disabled
          placeholder="Sector"
          labelClassName={FORM_LABEL_CLASS_NAME}
          triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
        />
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <FormSelectField
          id={`${fieldPrefix}-role`}
          label="Role Category"
          value={learner.roleCategory}
          onValueChange={(v) => update('roleCategory', v)}
          options={ROLE_CATEGORY_OPTIONS}
          disabled
          placeholder="Role Category"
          labelClassName={FORM_LABEL_CLASS_NAME}
          triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
        />
        <FormSelectField
          id={`${fieldPrefix}-employment`}
          label="Employment Status"
          value={learner.employmentStatus}
          onValueChange={(v) => update('employmentStatus', v)}
          options={EMPLOYMENT_STATUS_OPTIONS}
          disabled
          placeholder="Employment Status"
          labelClassName={FORM_LABEL_CLASS_NAME}
          triggerClassName={FORM_SELECT_TRIGGER_CLASS_NAME}
        />
      </div>
    </div>
  );
};

export const CompleteProfileStep2Page: FC = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: currentUser } = useCurrentUser();
  const email = resolveOnboardingEmail({
    locationState: location.state,
    searchParams,
    currentUser,
  });
  const postOnboardingRedirect = getPostOnboardingRedirect(currentUser);

  const { enterpriseUuid } = useEnterpriseUuid();
  const { mutateAsync: inviteLearner } = useInviteLearner(enterpriseUuid);
  const [learners, setLearners] = useState<LearnerEntry[]>([emptyLearner()]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [learnerFieldErrors, setLearnerFieldErrors] = useState<LearnerFieldErrors[]>([{}]);

  const updateLearner = (index: number, updated: LearnerEntry) => {
    setLearners((prev) =>
      prev.map((learner, learnerIndex) => (learnerIndex === index ? updated : learner))
    );
  };

  const clearLearnerError = (index: number, field: keyof LearnerFieldErrors) => {
    setLearnerFieldErrors((prev) =>
      prev.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: undefined } : entry
      )
    );
  };

  const validateLearners = (): boolean => {
    const errors: LearnerFieldErrors[] = learners.map((learner) => {
      const nextErrors: LearnerFieldErrors = {};

      if (learner.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(learner.email)) {
        nextErrors.email = 'Please enter a valid email address.';
      }

      return nextErrors;
    });

    setLearnerFieldErrors(errors);
    return errors.every((entry) => !entry.email);
  };

  const addLearner = () => {
    setLearners((prev) => [...prev, emptyLearner()]);
    setLearnerFieldErrors((prev) => [...prev, {}]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateLearners()) {
      return;
    }

    const learnersWithEmail = learners.filter((learner) => learner.email.trim());
    if (learnersWithEmail.length > 0 && !enterpriseUuid) {
      setError(MISSING_ENTERPRISE_MESSAGE);
      return;
    }

    setIsLoading(true);

    try {
      if (learnersWithEmail.length > 0) {
        // TODO: forward `username`, `contactNumber`, `sector`, `roleCategory`,
        // and `employmentStatus` once the backend `CreateLearner` schema accepts
        // them. See backend.request.md ("Learner invite — Complete Profile step 2 fields").
        await Promise.all(
          learnersWithEmail.map((learner) => {
            const nameParts = learner.name.trim().split(/\s+/);
            const first_name = nameParts[0] ?? '';
            const last_name = nameParts.slice(1).join(' ');
            return inviteLearner({ first_name, last_name, email: learner.email });
          })
        );
      }

      window.location.href = postOnboardingRedirect;
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Failed to add learners. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    window.location.href = postOnboardingRedirect;
  };

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-start py-8 px-4">
      <div className="mb-6">
        <img src={logoImage} alt="Learnovate360" className="h-28.75 w-61 object-contain" />
      </div>

      <div className="animate-fade-in-down bg-white rounded-[8px] shadow-elevation-1 w-full max-w-187 px-12 pt-[26px] pb-[25px] mb-8">
        <Link
          to={buildOnboardingPath('/signup/complete-profile', { email })}
          className="inline-flex items-center gap-2.5 text-lg font-medium text-brand-primary leading-6 hover:underline mb-6"
        >
          <Icon src={arrowRightIcon} size="lg" className="text-brand-primary -scale-x-100" />
          Go Back
        </Link>

        <div className="flex items-center justify-between mb-3">
          <Heading as="h2" className="text-[22px] font-medium text-black leading-7">
            Add Learners
          </Heading>
          <Button
            type="button"
            variant="brandOutline"
            disabled
            title="Bulk upload is not available yet"
            className="h-11 rounded px-4 py-2.5 gap-2 text-lg font-medium leading-6 disabled:opacity-60"
          >
            <Icon src={uploadIcon} size="lg" className="text-current" />
            Bulk Upload
          </Button>
        </div>

        <CollapsibleContainer isOpen={!!error} className="mb-6">
          <Alert variant="destructive" onDismiss={() => setError(null)}>
            {error || ''}
          </Alert>
        </CollapsibleContainer>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-6 mb-4">
            {learners.map((learner, index) => (
              <LearnerForm
                key={learner._uid}
                index={index}
                learner={learner}
                onChange={(updated) => updateLearner(index, updated)}
                disabled={isLoading}
                fieldErrors={learnerFieldErrors[index]}
                onClearError={(field) => clearLearnerError(index, field)}
              />
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={addLearner}
            disabled={isLoading}
            className="w-full h-11 rounded px-4 py-2.5 gap-2 mb-6 bg-white border-dashed border-brand-primary text-brand-primary text-lg font-medium leading-6 hover:bg-brand-tint disabled:opacity-60"
          >
            <Icon src={plusIcon} size="lg" className="text-current" />
            Add More
          </Button>

          <div className="grid grid-cols-2 gap-2.5">
            <Button
              type="button"
              variant="ghost"
              onClick={handleSkip}
              disabled={isLoading}
              className="h-11 rounded px-4 py-2.5 text-lg font-medium leading-6 text-brand-primary bg-brand-tint hover:bg-brand-tint/80"
            >
              Skip for now
            </Button>
            <Button
              type="submit"
              variant="auth"
              disabled={isLoading}
              className="h-11 rounded px-4 py-2.5 text-lg font-medium leading-6"
            >
              {isLoading ? 'Saving...' : 'Continue'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
